import React from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { SearchCategory } from 'constants/enums'
import { useTheme } from 'styled-components'
import {
  InlineCell,
  PageBody,
  TopRow,
  TopTitle,
  TopGrid,
  CategoriesWrap,
  TitleContent,
  TitleIconWrapper
} from '../Search.styles'
import {
  ArtistSearchCard,
  LabelSearchCard,
  ReleaseSearchCard,
  TrackSearchCard
} from 'components/SearchCards'
import { Tonality } from '@dsny/dsny-component-library'
import { Help } from '@dsny/dsny-component-library/icons'
import {
  RESPONSIVE_BREAKPOINT_VERSIONS,
  useResponsiveBreakpointsVersion
} from 'contexts/ResponsiveBreakpointsContext'

function TopResultsDesktop({
  results = [],
  useAISearch = false,
  loading,
  renderNoResults,
  onShowModal,
  onPlayRelease,
  onDownloadRelease,
  onPlayTrack,
  onDownloadTrack,
  renderCategory
}) {
  const { t } = useTranslation()
  const theme = useTheme()
  const responsiveBreakpointsVersion = useResponsiveBreakpointsVersion()
  const responsiveBreakpointsV2 =
    responsiveBreakpointsVersion === RESPONSIVE_BREAKPOINT_VERSIONS.V2
  const renderTopResultCell = ({ item, type }, index) => {
    if (!item) return null

    const baseKey =
      item.trackid ||
      item.releaseid ||
      item.artistid ||
      item.labelid ||
      index + 1

    switch (type) {
      case SearchCategory.tracks:
        return (
          <InlineCell key={`${type}-${baseKey}`}>
            <TrackSearchCard
              track={item}
              onShowModal={onShowModal}
              handlePlayTrack={onPlayTrack}
              handleDownloadTrack={onDownloadTrack}
            />
          </InlineCell>
        )
      case SearchCategory.releases:
        return (
          <InlineCell key={`${type}-${baseKey}`}>
            <ReleaseSearchCard
              release={item}
              onShowModal={onShowModal}
              handlePlayRelease={onPlayRelease}
              handleDownloadRelease={onDownloadRelease}
            />
          </InlineCell>
        )
      case SearchCategory.artists:
        return (
          <InlineCell key={`${type}-${baseKey}`}>
            <ArtistSearchCard artist={item} />
          </InlineCell>
        )
      case SearchCategory.labels:
        return (
          <InlineCell key={`${type}-${baseKey}`}>
            <LabelSearchCard label={item} />
          </InlineCell>
        )
      default:
        return null
    }
  }

  const topCells = results.map(renderTopResultCell).filter(Boolean)
  const hasResults = topCells.length > 0

  if (!hasResults && !loading) return renderNoResults()

  const aiTooltipText = t('search.ai-results-tooltip')

  return (
    <PageBody>
      <TopRow $responsiveBreakpointsV2={responsiveBreakpointsV2}>
        <TopTitle $responsiveBreakpointsV2={responsiveBreakpointsV2}>
          {useAISearch ? (
            <TitleContent>
              {t('search.ai-results')}
              <Tonality.Tooltip
                mode="dark"
                hhasArrow={false}
                placement="top"
                content={aiTooltipText}
                trigger="hover"
                containerStyles={{ padding: '4px 8px', maxWidth: 260 }}
                children={
                  <span>
                    <TitleIconWrapper aria-label={aiTooltipText} role="button">
                      <Help
                        width="20px"
                        height="20px"
                        color={theme.colors.white0}
                      />
                    </TitleIconWrapper>
                  </span>
                }
              />
            </TitleContent>
          ) : (
            t('search.top-results')
          )}
        </TopTitle>
        <TopGrid data-cy="search-top-results">{topCells}</TopGrid>
      </TopRow>
      <CategoriesWrap $responsiveBreakpointsV2={responsiveBreakpointsV2}>
        {renderCategory('tracks', t('search.songs'))}
        {renderCategory('artists', t('search.artists'))}
        {renderCategory('releases', t('search.releases'))}
        {renderCategory('labels', t('search.labels'))}
      </CategoriesWrap>
    </PageBody>
  )
}

TopResultsDesktop.propTypes = {
  results: PropTypes.arrayOf(
    PropTypes.shape({
      item: PropTypes.object,
      type: PropTypes.oneOf([
        SearchCategory.tracks,
        SearchCategory.releases,
        SearchCategory.artists,
        SearchCategory.labels
      ])
    })
  ).isRequired,
  useAISearch: PropTypes.bool,
  loading: PropTypes.bool,
  renderNoResults: PropTypes.func.isRequired,
  onShowModal: PropTypes.func.isRequired,
  onPlayRelease: PropTypes.func.isRequired,
  onDownloadRelease: PropTypes.func.isRequired,
  onPlayTrack: PropTypes.func.isRequired,
  onDownloadTrack: PropTypes.func.isRequired,
  renderCategory: PropTypes.func.isRequired
}

export default TopResultsDesktop
